import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useToast, toastErrorMessage } from "../lib/toast";
import Sheet from "../components/Sheet";
import TimePicker from "../components/TimePicker";
import { fmtPhone, telHref } from "../lib/format";
import { TASK_TYPES } from "../lib/taskTypes";
import { Phone, Mail, Plus, Search, UserPlus } from "lucide-react";

export default function Contacts() {
  const { meta } = useAuth();
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dm, setDm] = useState("");
  const [state, setState] = useState("");
  const [taskFor, setTaskFor] = useState(null); // {contact, company_id}
  const [pickCompany, setPickCompany] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (dm) p.set("decision_maker", dm);
    const { data } = await api.get(`/contacts?${p}`);
    setRows(data); setLoading(false);
  }, [q, dm]);
  useEffect(() => {
    const h = setTimeout(reload, 200);
    return () => clearTimeout(h);
  }, [reload]);

  const filtered = useMemo(
    () => state ? rows.filter(r => r.company_state === state) : rows,
    [rows, state]
  );

  return (
    <div className="space-y-4" data-testid="contacts-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Contacts</h1>
          <p className="text-gray-500 text-sm">{filtered.length} contact{filtered.length !== 1 ? "s" : ""} across the customer base</p>
        </div>
        <button className="cesco-btn-primary flex items-center gap-2 shrink-0"
                onClick={()=>setPickCompany(true)}
                data-testid="btn-create-contact">
          <UserPlus size={16}/> Create contact
        </button>
      </div>

      <div className="cesco-card p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="relative col-span-2 md:col-span-2">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input className="cesco-input pl-9" placeholder="Search name, email, phone, position…"
                 value={q} onChange={e=>setQ(e.target.value)} data-testid="contacts-filter-q"/>
        </div>
        <select className="cesco-input" value={dm} onChange={e=>setDm(e.target.value)}
                data-testid="contacts-filter-dm">
          <option value="">Any role</option>
          <option value="Yes">Decision maker</option>
          <option value="No">Influencer / other</option>
        </select>
        <select className="cesco-input" value={state} onChange={e=>setState(e.target.value)}
                data-testid="contacts-filter-state">
          <option value="">All states</option>
          {meta?.au_states.map(s => <option key={s.code} value={s.code}>{s.code}</option>)}
        </select>
      </div>

      <div className="cesco-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Position</th>
                <th className="text-left px-4 py-3">Company</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Email</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Mobile</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="p-4"><div className="skeleton h-6 w-full"/></td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-gray-500">No contacts match</td></tr>
              )}
              {filtered.map(c => (
                <tr key={c.id} className="border-t hover:bg-[var(--theme-accent-50)]" data-testid="contact-row">
                  <td className="px-4 py-3">
                    <Link to={`/companies/${c.company_id}?tab=Contacts`} className="font-medium hover:underline"
                          data-testid={`link-contact-${c.id}`}>
                      {c.contact_title ? c.contact_title + " " : ""}{c.first_name} {c.last_name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{c.position || "-"}</td>
                  <td className="px-4 py-3">
                    <Link to={`/companies/${c.company_id}`} className="hover:underline">
                      {c.company_name}
                    </Link>
                    <div className="text-xs text-gray-500">{c.company_state}</div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {c.direct_email
                      ? <a className="text-blue-700 hover:underline inline-flex items-center gap-1" href={`mailto:${c.direct_email}`}><Mail size={12}/> {c.direct_email}</a>
                      : <span className="text-gray-400">-</span>}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {c.phone_mobile
                      ? <a className="text-blue-700 hover:underline inline-flex items-center gap-1" href={telHref(c.phone_mobile)}><Phone size={12}/> {fmtPhone(c.phone_mobile)}</a>
                      : c.phone_office
                        ? <a className="text-blue-700 hover:underline inline-flex items-center gap-1" href={telHref(c.phone_office)}><Phone size={12}/> {fmtPhone(c.phone_office)}</a>
                        : <span className="text-gray-400">-</span>}
                  </td>
                  <td className="px-4 py-3">
                    {c.decision_maker === "Yes"
                      ? <span className="pill pill-green">Decision maker</span>
                      : c.decision_maker === "No"
                        ? <span className="pill pill-grey">Influencer</span>
                        : <span className="text-gray-400 text-xs">-</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button className="cesco-btn-ghost text-xs inline-flex items-center gap-1"
                            onClick={()=>setTaskFor(c)}
                            data-testid="btn-contact-task">
                      <Plus size={12}/> Task
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {taskFor && (
        <QuickTaskModal contact={taskFor} onClose={()=>setTaskFor(null)}
                        onSaved={()=>toast.success("Task created")} toast={toast}/>
      )}
      {pickCompany && (
        <PickCompanyModal onClose={()=>setPickCompany(false)}/>
      )}
    </div>
  );
}

function PickCompanyModal({ onClose }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const h = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/companies?q=${encodeURIComponent(q)}&limit=20`);
        setResults(data);
      } catch { /* debounced lookup - silent to avoid console spam on rapid typing */ }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(h);
  }, [q]);

  function choose(co) {
    onClose();
    navigate(`/companies/${co.id}?tab=Contacts&new_contact=1`);
  }

  return (
    <Sheet open onClose={onClose} title="Add contact - pick a company first"
           footer={<div className="flex justify-end"><button className="cesco-btn-ghost" onClick={onClose}>Cancel</button></div>}>
      <div className="space-y-3">
        <p className="text-sm text-gray-600">Search for the company this contact belongs to.</p>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input autoFocus className="cesco-input pl-9" placeholder="Type a company name…"
                 value={q} onChange={e=>setQ(e.target.value)}
                 data-testid="pick-company-input"/>
        </div>
        <div className="border rounded-md divide-y max-h-80 overflow-y-auto">
          {loading && <div className="p-3 text-sm text-gray-500">Searching…</div>}
          {!loading && results.length === 0 && (
            <div className="p-3 text-sm text-gray-500">No companies match. Start typing to search.</div>
          )}
          {results.map(co => (
            <button key={co.id} className="block w-full text-left px-3 py-2 hover:bg-blue-50"
                    onClick={()=>choose(co)}
                    data-testid={`pick-company-${co.id}`}>
              <div className="font-medium">{co.company_name}</div>
              <div className="text-xs text-gray-500">{co.suburb || ""}{co.suburb && co.state ? " · " : ""}{co.state || ""}{co.tier ? ` · Tier ${co.tier}` : ""}</div>
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500">
          Don't see the company? <Link to="/companies" onClick={onClose} className="text-blue-700 hover:underline">Create it first on the Companies page</Link>.
        </p>
      </div>
    </Sheet>
  );
}

function QuickTaskModal({ contact, onClose, onSaved, toast }) {
  const [summary, setSummary] = useState("");
  const [due, setDue] = useState("");
  const [time, setTime] = useState("");
  const [taskType, setTaskType] = useState("Phone Call");
  const [err, setErr] = useState("");
  async function save() {
    setErr("");
    if (!summary.trim() || !due) { setErr("Summary and due date are required"); return; }
    try {
      await api.post(`/companies/${contact.company_id}/tasks`, {
        summary: summary.trim(), due_date: due, due_time: time || null,
        contact_id: contact.id,
        task_type: taskType,
      });
      onSaved?.(); onClose();
    } catch(e) {
      const m = toastErrorMessage(e); setErr(m); toast.error(m);
    }
  }
  return (
    <Sheet open onClose={onClose}
           title={`Task for ${contact.first_name} ${contact.last_name}`}
           footer={<div className="flex gap-2 justify-end"><button className="cesco-btn-ghost" onClick={onClose}>Cancel</button>
                      <button className="cesco-btn-primary" onClick={save} data-testid="quick-task-save">Save</button></div>}>
      <div className="space-y-3">
        <div className="text-sm text-gray-600">
          Linked to <b>{contact.company_name}</b> · <b>{contact.first_name} {contact.last_name}</b>
        </div>
        <div>
          <label className="cesco-label">Type *</label>
          <select className="cesco-input" value={taskType} onChange={e=>setTaskType(e.target.value)}
                  data-testid="quick-task-type">
            {TASK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="cesco-label">Summary *</label>
          <textarea className="cesco-input" rows={3} value={summary}
                    onChange={e=>setSummary(e.target.value)} data-testid="quick-task-summary"/>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="cesco-label">Due date *</label>
            <input className="cesco-input" type="date" value={due}
                   onChange={e=>setDue(e.target.value)} data-testid="quick-task-due"/>
          </div>
          <div>
            <label className="cesco-label">Due time</label>
            <TimePicker value={time} onChange={setTime} testId="quick-task-time"/>
          </div>
        </div>
        {err && <div className="text-sm text-red-600">{err}</div>}
      </div>
    </Sheet>
  );
}
